import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Store, Search, Star, MapPin, Tag } from 'lucide-react';

interface Supplier {
  id: string;
  name: string;
  category: string;
  description: string;
  logo_url?: string;
  rating?: number;
  city?: string;
  is_verified?: boolean;
}

/**
 * Marketplace — Supplier/business directory for the Services lens.
 * Combines condo-scoped suppliers with general business listings.
 */
const Marketplace: React.FC = () => {
  const { user } = useAuth();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');

  const condoId = user?.condoId;

  useEffect(() => {
    (async () => {
      try {
        const params = condoId ? `/businesses/condo/${condoId}` : '/businesses';
        const res = await api.get(params);
        const data = res.data?.data ?? res.data ?? [];
        setSuppliers(Array.isArray(data) ? data : []);
      } catch (e) {
        setSuppliers([]);
      } finally { setLoading(false); }
    })();
  }, [condoId]);

  const categories = [...new Set(suppliers.map(s => s.category).filter(Boolean))];

  const filtered = suppliers.filter(s => {
    if (category && s.category !== category) return false;
    if (search) {
      const q = search.toLowerCase();
      return s.name?.toLowerCase().includes(q) || s.description?.toLowerCase().includes(q) || s.category?.toLowerCase().includes(q);
    }
    return true;
  });

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--v-teal-700)]" />
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto">
     
      <h2 className="text-2xl font-bold text-[var(--v-text-primary)] mb-4">Marketplace</h2>

      {/* Search + Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--v-text-tertiary)]" />
          <input
            type="text"
            placeholder="Buscar servicios..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-[var(--v-surface-1)] border border-[var(--v-border-light)] text-[var(--v-text-primary)] placeholder-[var(--v-text-tertiary)] focus:outline-none focus:border-[var(--v-teal-700)]"
          />
        </div>
        {categories.length > 0 && (
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            className="px-4 py-2 rounded-lg bg-[var(--v-surface-1)] border border-[var(--v-border-light)] text-[var(--v-text-primary)] focus:outline-none focus:border-[var(--v-teal-700)]"
          >
            <option value="">Todas las categorías</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </div>

      {error && <p className="text-[var(--v-danger)] text-sm mb-4">{error}</p>}

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Store className="w-12 h-12 text-[var(--v-text-secondary)] mb-3" />
          <p className="text-[var(--v-text-secondary)] text-lg">No hay servicios disponibles</p>
          <p className="text-[var(--v-text-tertiary)] text-sm mt-1">Los proveedores registrados aparecerán aquí.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(s => (
            <div key={s.id} className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-12 h-12 rounded-lg bg-[var(--v-surface-2)] flex items-center justify-center flex-shrink-0">
                  {s.logo_url ? (
                    <img src={s.logo_url} alt={s.name} className="w-full h-full rounded-lg object-cover" />
                  ) : (
                    <Store className="w-6 h-6 text-[var(--v-teal-700)]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-white truncate">{s.name}</h3>
                  {s.category && <p className="text-xs text-[var(--v-teal-700)] flex items-center gap-1"><Tag className="w-3 h-3" />{s.category}</p>}
                </div>
                {s.is_verified && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Verificado</span>}
              </div>
              {s.description && <p className="text-sm text-[var(--v-text-secondary)] line-clamp-2 mb-3">{s.description}</p>}
              <div className="flex items-center justify-between text-xs text-[var(--v-text-tertiary)]">
                {s.rating != null && <span className="flex items-center gap-1"><Star className="w-3 h-3 text-amber-400" />{Number(s.rating).toFixed(1)}</span>}
                {s.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{s.city}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Marketplace;
